import { useId, type InputHTMLAttributes } from 'react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /** force LTR rendering for codes / numbers / usernames inside an RTL form */
  ltr?: boolean;
  /** label only for screen readers: inputs in a grid under one visible column header */
  labelHidden?: boolean;
}

/** Labelled input with accessible error association (11 §1 accessibility basics). */
export function TextField({
  label,
  error,
  hint,
  ltr,
  labelHidden,
  className = '',
  ...rest
}: TextFieldProps) {
  const id = useId();
  const describedBy =
    [error ? `${id}-err` : null, hint ? `${id}-hint` : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className={labelHidden ? 'sr-only' : 'text-sm font-medium text-text'}>
        {label}
        {rest.required ? (
          <span aria-hidden="true" className="text-danger ms-1">
            *
          </span>
        ) : null}
      </label>
      <input
        id={id}
        {...rest}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        dir={ltr ? 'ltr' : undefined}
        className={[
          'h-10 rounded-md border bg-surface px-3 text-base text-text',
          error ? 'border-danger' : 'border-border',
          ltr ? 'text-start' : '',
          className,
        ].join(' ')}
      />
      {hint ? (
        <p id={`${id}-hint`} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-err`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
